import React, { useContext, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';

// CONTEXTS
import { TabsWrapper, TabsHeader, TabPanel } from '@warehouse/shared/ui';
import { TitleFull } from '@warehouse/title/core';
import { UnsavedChangesContext } from '../contexts/UnsavedChangesContext';

// LIBRARY
import { SideModal } from '../../../components/library/SideDrawer';

// TABS
import { GeneralTab } from './GeneralTab';
import { SystemSettingsTab } from './SystemSettingsTab';
import { PaddingWrapper } from './style';

interface TitleSettingsProps {
	open: boolean;
	handleClose: () => void;
	data?: TitleFull;
	wrapperRef: React.RefObject<HTMLDivElement>;
	selectedTabQuery: string;
	onSettingsTabChange: (value: string | null) => void;
}

const GlobalStyle = createGlobalStyle`
	body {
		overflow: hidden;
	}
`;

const tabs = [
	{ key: 'general', label: 'General', component: GeneralTab },
	{ key: 'system', label: 'System Settings', component: SystemSettingsTab },
];

export default function TitleSettings({
	open,
	handleClose,
	data,
	wrapperRef,
	selectedTabQuery,
	onSettingsTabChange,
}: TitleSettingsProps) {
	const lowerQuery = selectedTabQuery?.toLowerCase();
	const selectedTab = Math.max(
		0,
		tabs.findIndex((t) => t.key === lowerQuery),
	);
	const handleTabChange = (tabIndex: number) => {
		onSettingsTabChange(tabs[tabIndex].key);
	};
	const { promptUserIfUnsavedChanges } = useContext(UnsavedChangesContext);

	useEffect(() => {
		if (!lowerQuery) return;

		const isKnownTab = tabs.some((t) => t.key === lowerQuery);
		if (!isKnownTab) {
			onSettingsTabChange(tabs[0].key);
		} else if (selectedTabQuery !== lowerQuery) {
			onSettingsTabChange(lowerQuery);
		}
	}, [lowerQuery, selectedTabQuery, onSettingsTabChange]);

	const overrideHandleClose = () => {
		promptUserIfUnsavedChanges(() => handleClose());
	};

	return (
		<SideModal
			wrapperRef={wrapperRef}
			side="right"
			open={open}
			handleClose={overrideHandleClose}
			title="Title Settings"
		>
			<GlobalStyle />

			<TabsWrapper>
				<TabsHeader selectedTab={selectedTab} onChange={handleTabChange} prefix="title-settings" tabs={tabs} />

				{tabs.map((tab, index) => {
					const Component = tab.component;

					return (
						<TabPanel key={tab.label} prefix="title-settings" value={selectedTab} index={index}>
							<PaddingWrapper aria-label="title-settings-scroll-container">
								<Component data={data} />
							</PaddingWrapper>
						</TabPanel>
					);
				})}
			</TabsWrapper>
		</SideModal>
	);
}
